import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { type MeResponse, type TrafficRow, api } from "../lib/api";

type Me = MeResponse & {
	org: { id: string; kind: "brand" | "affiliate" } | null;
};

export function TrafficDetail() {
	const { id = "" } = useParams();
	const nav = useNavigate();
	const qc = useQueryClient();
	const profileQ = useQuery<TrafficRow>({
		queryKey: ["traffic", id],
		queryFn: () => api.get(`/traffic/${id}`),
		enabled: !!id,
	});
	const meQ = useQuery<Me>({ queryKey: ["me"], queryFn: () => api.get("/me") });

	const publishMut = useMutation({
		mutationFn: () => api.post(`/traffic/${id}/publish`, {}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["traffic", id] });
			qc.invalidateQueries({ queryKey: ["traffic"] });
		},
	});

	if (profileQ.isLoading) return <div className="p-6">Loading…</div>;
	if (profileQ.error)
		return <div className="p-6 text-red-700">Error: {(profileQ.error as Error).message}</div>;
	if (!profileQ.data) return <div className="p-6 text-neutral-500">Not found</div>;
	const t = profileQ.data;
	const myOrg = meQ.data?.org;
	const own = myOrg?.kind === "affiliate" && myOrg.id === t.affiliateOrgId;

	return (
		<div className="space-y-4 p-6">
			<div>
				<Link to="/traffic" className="text-sm text-blue-700 hover:underline">
					← Traffic profiles
				</Link>
			</div>
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold">{t.displayName}</h1>
					<div className="font-mono text-xs text-neutral-500">{t.id}</div>
				</div>
				<div className="flex gap-2">
					{own && t.status === "draft" && (
						<>
							<button
								type="button"
								onClick={() => nav(`/traffic/${t.id}/edit`)}
								className="rounded border border-neutral-300 bg-white px-3 py-1 text-sm hover:bg-neutral-50"
							>
								Edit
							</button>
							<button
								type="button"
								onClick={() => publishMut.mutate()}
								className="rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700"
							>
								Publish
							</button>
						</>
					)}
				</div>
			</div>
			<dl className="grid grid-cols-2 gap-2 text-sm">
				<Field label="Vertical" value={t.vertical} />
				<Field label="Status" value={t.status} />
				<Field label="Subverticals" value={safeArr(t.subverticals).join(", ")} />
				<Field label="Geos" value={safeArr(t.geos).join(", ")} />
				<Field label="Sources" value={safeArr(t.sources).join(", ")} />
				<Field label="Monthly volume" value={t.monthlyVolume ?? "—"} />
				<Field label="Avg conv. rate" value={t.avgConversionRate ?? "—"} />
				<Field label="Fraud score" value={t.fraudScore ?? "—"} />
				<Field label="Created" value={new Date(t.createdAt * 1000).toISOString().slice(0, 19)} />
			</dl>
		</div>
	);
}

function Field({ label, value }: { label: string; value: string | number }) {
	return (
		<div>
			<dt className="text-xs text-neutral-500">{label}</dt>
			<dd className="text-neutral-900">{value}</dd>
		</div>
	);
}

function safeArr(s: string | null): string[] {
	if (!s) return [];
	try {
		const v = JSON.parse(s);
		return Array.isArray(v) ? (v as string[]) : [];
	} catch {
		return [];
	}
}
